import { Link, Navigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Hourglass, AlertTriangle, Lock, CreditCard, ShieldOff } from "lucide-react";

const fmt = (d) => (d ? new Date(d + "T12:00:00Z").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "");

export function AccessBanner({ user }) {
  const acc = user?.access;
  if (user?.role !== "landlord" || !acc || acc.super) return null;
  const days = acc.days_left;
  if (acc.state === "active" && acc.in_trial)
    return (
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900" data-testid="access-banner" data-state="trial">
        <Hourglass className="h-4 w-4 shrink-0" />
        <span className="flex-1 min-w-[200px]"><b>Free trial</b> · {days} day{days === 1 ? "" : "s"} left · ends {fmt(acc.until)}.</span>
        <Link to="/billing" className="text-xs font-semibold underline underline-offset-2" data-testid="access-banner-link">See your plan</Link>
      </div>
    );
  if (acc.state === "ending_soon")
    return (
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="access-banner" data-state="ending_soon">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="flex-1 min-w-[200px]"><b>Your access ends {days === 0 ? "today" : `in ${days} day${days === 1 ? "" : "s"}`}</b> ({fmt(acc.until)}). Pay this month's fee to keep going without interruption.</span>
        <Button asChild size="sm" className="h-8 gap-1.5"><Link to="/billing" data-testid="access-banner-link"><CreditCard className="h-3.5 w-3.5" /> Go to Billing</Link></Button>
      </div>
    );
  if (acc.state === "expired")
    return (
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900" data-testid="access-banner" data-state="expired">
        <Lock className="h-4 w-4 shrink-0" />
        <span className="flex-1 min-w-[200px]"><b>Your access ended {fmt(acc.until)}.</b> Your houses, tenants and records are safe — pay this month's management fee below to unlock your account.</span>
      </div>
    );
  return null;
}

export function AccessGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const acc = user?.access;
  if (user?.role !== "landlord" || !acc || acc.super) return children;
  if (acc.state === "suspended")
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center max-w-lg mx-auto" data-testid="account-suspended">
        <ShieldOff className="h-10 w-10 text-rose-500 mx-auto mb-3" />
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Account suspended</h1>
        <p className="text-sm text-slate-500 mt-2">This VuZuRent account has been suspended by the administrator. Your data is safe. Please contact VuZuRent to restore access.</p>
      </div>
    );
  if (acc.state === "expired" && !location.pathname.startsWith("/billing")) return <Navigate to="/billing?locked=1" replace />;
  return children;
}
